import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Textarea,
  VStack,
  Text,
  HStack,
  Badge,
} from '@chakra-ui/react';
import axiosInstance from '../../../api/AxiosInstance';
import { fetchLocalidadesCatalogo, LocalidadOption } from '../../../utils/localidadesCatalogo';

const getBaseOrigin = () => {
  const configured = axiosInstance.defaults.baseURL as string | undefined;
  if (!configured) return window.location.origin;
  try {
    return new URL(configured, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
};

const resolveAssetUrl = (value: string) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${getBaseOrigin()}${value.startsWith('/') ? '' : '/'}${value}`;
};

interface Props {
  modo: 'new' | 'edit' | 'view';
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const FormularioBeneficiario: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const [form, setForm] = useState<any>({
    curp: '',
    nombreCompleto: '',
    fechaNacimiento: '',
    sexo: '',
    estadoCivil: '',
    localidadId: '',
    domicilioCompleto: '',
    telefono: '',
    correo: '',
    nivelEducativo: '',
    ocupacion: '',
    ingresoMensual: '',
    integrantesHogar: '',
    tipoVulnerabilidad: '',
    estatus: 'pendiente',
  });
  const [guardando, setGuardando] = useState(false);
  const [localidades, setLocalidades] = useState<LocalidadOption[]>([]);
  const [files, setFiles] = useState<any>({
    ine: null,
    curp: null,
    domicilio: null,
    solicitud: null,
    foto: null,
  });

  useEffect(() => {
    fetchLocalidadesCatalogo().then(setLocalidades).catch(() => setLocalidades([]));
  }, []);

  useEffect(() => {
    if (data) {
      setForm({
        curp: data.curp || '',
        nombreCompleto: data.nombreCompleto || '',
        fechaNacimiento: data.fechaNacimiento ? data.fechaNacimiento.split('T')[0] : '',
        sexo: data.sexo || '',
        estadoCivil: data.estadoCivil || '',
        localidadId: data.localidadId || '',
        domicilioCompleto: data.domicilioCompleto || '',
        telefono: data.telefono || '',
        correo: data.correo || '',
        nivelEducativo: data.nivelEducativo || '',
        ocupacion: data.ocupacion || '',
        ingresoMensual: data.ingresoMensual || '',
        integrantesHogar: data.integrantesHogar || '',
        tipoVulnerabilidad: data.tipoVulnerabilidad || '',
        estatus: data.estatus || 'pendiente',
      });
    }
  }, [data]);

  const handleChange = (field: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleFile = (field: string, file: File | null) => {
    setFiles((prev: any) => ({ ...prev, [field]: file }));
  };

  const handleSubmit = async () => {
    if (!form.curp || !form.nombreCompleto || !form.fechaNacimiento || !form.localidadId) return;
    setGuardando(true);
    try {
      if (modo === 'new') {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, String(v ?? '')));
        Object.entries(files).forEach(([k, v]) => {
          if (v) fd.append(k, v as any);
        });
        await axiosInstance.post('/desarrollo-social/beneficiarios', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else if (modo === 'edit' && data?.id) {
        await axiosInstance.put(`/desarrollo-social/beneficiarios/${data.id}`, form);
      }
      onGuardado && onGuardado();
    } finally {
      setGuardando(false);
    }
  };

  const disabled = modo === 'view';

  return (
    <Box>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel>CURP</FormLabel>
          <Input value={form.curp} onChange={(e) => handleChange('curp', e.target.value.toUpperCase())} isDisabled={disabled} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Nombre completo</FormLabel>
          <Input value={form.nombreCompleto} onChange={(e) => handleChange('nombreCompleto', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Fecha nacimiento</FormLabel>
          <Input type="date" value={form.fechaNacimiento} onChange={(e) => handleChange('fechaNacimiento', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Sexo</FormLabel>
          <Select value={form.sexo} onChange={(e) => handleChange('sexo', e.target.value)} isDisabled={disabled}>
            <option value="">Selecciona</option>
            <option value="M">Masculino</option>
            <option value="F">Femenino</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Estado civil</FormLabel>
          <Input value={form.estadoCivil} onChange={(e) => handleChange('estadoCivil', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Localidad</FormLabel>
          <Select value={String(form.localidadId || '')} onChange={(e) => handleChange('localidadId', e.target.value)} isDisabled={disabled}>
            <option value="">Selecciona localidad</option>
            {localidades.map((loc) => (
              <option key={loc.id} value={loc.id}>{loc.label}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Domicilio completo</FormLabel>
          <Textarea value={form.domicilioCompleto} onChange={(e) => handleChange('domicilioCompleto', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Telefono</FormLabel>
          <Input value={form.telefono} onChange={(e) => handleChange('telefono', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Correo</FormLabel>
          <Input value={form.correo} onChange={(e) => handleChange('correo', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Nivel educativo</FormLabel>
          <Input value={form.nivelEducativo} onChange={(e) => handleChange('nivelEducativo', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Ocupacion</FormLabel>
          <Input value={form.ocupacion} onChange={(e) => handleChange('ocupacion', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Ingreso mensual</FormLabel>
          <Input type="number" value={form.ingresoMensual} onChange={(e) => handleChange('ingresoMensual', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Integrantes hogar</FormLabel>
          <Input type="number" value={form.integrantesHogar} onChange={(e) => handleChange('integrantesHogar', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Tipo vulnerabilidad</FormLabel>
          <Input value={form.tipoVulnerabilidad} onChange={(e) => handleChange('tipoVulnerabilidad', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Estatus</FormLabel>
          <Select value={form.estatus} onChange={(e) => handleChange('estatus', e.target.value)} isDisabled={disabled}>
            <option value="aprobado">Aprobado</option>
            <option value="no_aprobado">No aprobado</option>
            <option value="pendiente">Pendiente</option>
          </Select>
        </FormControl>
      </SimpleGrid>

      {modo === 'view' && data?.documentos && (
        <Box mt={6}>
          <Text fontWeight="bold" mb={2}>Documentos</Text>
          <VStack align="stretch" spacing={2}>
            {data.documentos.map((doc: any) => (
              <HStack key={doc.id} justify="space-between">
                <Badge>{doc.tipo}</Badge>
                <a href={resolveAssetUrl(doc.url)} target="_blank" rel="noreferrer">Ver archivo</a>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}

      {modo === 'view' && data?.apoyos && (
        <Box mt={6}>
          <Text fontWeight="bold" mb={2}>Historial de apoyos</Text>
          <VStack align="stretch" spacing={2}>
            {data.apoyos.map((ap: any) => (
              <Box key={ap.id} p={3} borderWidth="1px" borderRadius="md">
                <Text fontSize="sm">Programa: {ap.programa?.nombre || '-'}</Text>
                <Text fontSize="sm">Tipo: {ap.tipoApoyo}</Text>
                <Text fontSize="sm">Fecha: {ap.fechaEntrega ? new Date(ap.fechaEntrega).toLocaleDateString('es-MX') : '-'}</Text>
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      {modo === 'new' && (
        <Box mt={6}>
          <Text fontWeight="bold" mb={2}>Documentos</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>INE</FormLabel>
              <Input type="file" onChange={(e) => handleFile('ine', e.target.files?.[0] || null)} />
            </FormControl>
            <FormControl>
              <FormLabel>CURP</FormLabel>
              <Input type="file" onChange={(e) => handleFile('curp', e.target.files?.[0] || null)} />
            </FormControl>
            <FormControl>
              <FormLabel>Comprobante domicilio</FormLabel>
              <Input type="file" onChange={(e) => handleFile('domicilio', e.target.files?.[0] || null)} />
            </FormControl>
            <FormControl>
              <FormLabel>Solicitud</FormLabel>
              <Input type="file" onChange={(e) => handleFile('solicitud', e.target.files?.[0] || null)} />
            </FormControl>
            <FormControl>
              <FormLabel>Foto</FormLabel>
              <Input type="file" onChange={(e) => handleFile('foto', e.target.files?.[0] || null)} />
            </FormControl>
          </SimpleGrid>
        </Box>
      )}

      {modo !== 'view' && (
        <Box mt={6} display="flex" gap={3} justifyContent="flex-end">
          <Button onClick={onCancelar} variant="outline">Cancelar</Button>
          <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleSubmit} isLoading={guardando} loadingText="Guardando">Guardar</Button>
        </Box>
      )}
    </Box>
  );
};

export default FormularioBeneficiario;

